import { baseApi } from './baseApi';

export const financeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getExpenseAccounts: builder.query({
      query: () => '/finance/expense-accounts',
      providesTags: ['Finance'],
    }),
    getExpenses: builder.query({
      query: (params) => ({
        url: '/finance/expenses',
        params,
      }),
      providesTags: ['Finance'],
    }),
    createExpense: builder.mutation({
      query: (data) => ({
        url: '/finance/expenses',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Finance', 'Report'],
    }),
    deleteExpense: builder.mutation({
      query: (id) => ({
        url: `/finance/expenses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Finance', 'Report'],
    }),
    getAccounts: builder.query({
      query: () => '/finance/accounts',
      providesTags: ['Finance'],
    }),
    createAccount: builder.mutation({
      query: (data) => ({
        url: '/finance/accounts',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Finance'],
    }),
    getBankAccounts: builder.query({
      query: () => '/finance/bank-accounts',
      providesTags: ['Finance'],
    }),
    getBankAccount: builder.query({
      query: (id) => `/finance/bank-accounts/${id}`,
      providesTags: (result, error, id) => [{ type: 'Finance', id: `bank-${id}` }],
    }),
    createBankAccount: builder.mutation({
      query: (data) => ({
        url: '/finance/bank-accounts',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Finance'],
    }),
    updateBankAccount: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/finance/bank-accounts/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Finance'],
    }),
    deleteBankAccount: builder.mutation({
      query: (id) => ({
        url: `/finance/bank-accounts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Finance'],
    }),
    getVouchers: builder.query({
      query: (params) => ({
        url: '/finance/vouchers',
        params: params || {},
      }),
      providesTags: ['Finance'],
    }),
    getVoucher: builder.query({
      query: (id) => `/finance/vouchers/${id}`,
      providesTags: (result, error, id) => [{ type: 'Finance', id: `voucher-${id}` }],
    }),
    createVoucher: builder.mutation({
      query: (data) => ({
        url: '/finance/vouchers',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Finance', 'Report'],
    }),
    updateVoucher: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/finance/vouchers/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Finance', 'Report'],
    }),
    deleteVoucher: builder.mutation({
      query: (id) => ({
        url: `/finance/vouchers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Finance', 'Report'],
    }),
    getChequebooks: builder.query({
      query: (params) => ({
        url: '/finance/chequebooks',
        params: params || {},
      }),
      providesTags: ['Finance'],
    }),
    getChequebook: builder.query({
      query: (id) => `/finance/chequebooks/${id}`,
      providesTags: (result, error, id) => [{ type: 'Finance', id: `chequebook-${id}` }],
    }),
    getChequebookLeaves: builder.query({
      query: (id) => `/finance/chequebooks/${id}/leaves`,
      providesTags: (result, error, id) => [{ type: 'Finance', id: `chequebook-leaves-${id}` }],
    }),
    getChequebookLeavesAvailable: builder.query({
      query: (id) => `/finance/chequebooks/${id}/leaves/available`,
      providesTags: (result, error, id) => [{ type: 'Finance', id: `chequebook-leaves-${id}` }],
    }),
    createChequebook: builder.mutation({
      query: (data) => ({
        url: '/finance/chequebooks',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Finance'],
    }),
    updateChequebook: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/finance/chequebooks/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Finance'],
    }),
    deleteChequebook: builder.mutation({
      query: (id) => ({
        url: `/finance/chequebooks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Finance'],
    }),
    getCheques: builder.query({
      query: (params) => ({
        url: '/finance/cheques',
        params: params || {},
      }),
      providesTags: ['Finance'],
    }),
    getCheque: builder.query({
      query: (id) => `/finance/cheques/${id}`,
      providesTags: (result, error, id) => [{ type: 'Finance', id: `cheque-${id}` }],
    }),
    createCheque: builder.mutation({
      query: (data) => ({
        url: '/finance/cheques',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Finance'],
    }),
    updateCheque: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/finance/cheques/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Finance'],
    }),
    deleteCheque: builder.mutation({
      query: (id) => ({
        url: `/finance/cheques/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Finance'],
    }),
  }),
});

export const {
  useGetExpensesQuery,
  useCreateExpenseMutation,
  useDeleteExpenseMutation,
  useGetExpenseAccountsQuery,
  useGetAccountsQuery,
  useCreateAccountMutation,
  useGetBankAccountsQuery,
  useGetBankAccountQuery,
  useCreateBankAccountMutation,
  useUpdateBankAccountMutation,
  useDeleteBankAccountMutation,
  useGetVouchersQuery,
  useGetVoucherQuery,
  useCreateVoucherMutation,
  useUpdateVoucherMutation,
  useDeleteVoucherMutation,
  useGetChequebooksQuery,
  useGetChequebookQuery,
  useGetChequebookLeavesQuery,
  useGetChequebookLeavesAvailableQuery,
  useCreateChequebookMutation,
  useUpdateChequebookMutation,
  useDeleteChequebookMutation,
  useGetChequesQuery,
  useGetChequeQuery,
  useCreateChequeMutation,
  useUpdateChequeMutation,
  useDeleteChequeMutation,
} = financeApi;

